import type { Rect } from '../../../engine/hex/types';
import { IconRegistry } from '../../../ui/overlay/assets/IconRegistry';
import type { ModalTheme } from '../ModalTheme';
import type { HitTarget, MeasureResult, PeopleListEntry, UiItem } from './Types';

export class PeopleListItem implements UiItem {
    private rect: Rect = { x: 0, y: 0, w: 0, h: 0 };
    private rowRects: Rect[] = [];
    private gap = 6;
    private padding = 6;
    private rowHeight = 64;
    private barHeight = 6;

    constructor(private items: PeopleListEntry[]) {}

    measure(_ctx: CanvasRenderingContext2D, maxW: number, theme: ModalTheme): MeasureResult {
        this.gap = theme.peopleList.gap;
        this.padding = theme.peopleList.padding;
        this.rowHeight = theme.peopleList.rowHeight;
        this.barHeight = theme.peopleList.barHeight;
        const height =
            this.items.length * this.rowHeight + this.gap * Math.max(0, this.items.length - 1);
        return { w: maxW, h: height };
    }

    layout(x: number, y: number, w: number, _theme: ModalTheme): void {
        const height =
            this.items.length * this.rowHeight + this.gap * Math.max(0, this.items.length - 1);
        this.rect = { x, y, w, h: height };
        this.rowRects = [];
        this.items.forEach((_, index) => {
            this.rowRects.push({
                x,
                y: y + index * (this.rowHeight + this.gap),
                w,
                h: this.rowHeight,
            });
        });
    }

    draw(ctx: CanvasRenderingContext2D, theme: ModalTheme, _icons?: IconRegistry): void {
        ctx.textBaseline = 'top';
        this.rowRects.forEach((row, index) => {
            const entry = this.items[index];
            if (!entry) return;
            ctx.fillStyle = theme.colors.tabInactiveFill;
            ctx.strokeStyle = theme.colors.tabStroke;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.rect(row.x, row.y, row.w, row.h);
            ctx.fill();
            ctx.stroke();

            const left = row.x + this.padding;
            const right = row.x + row.w - this.padding;
            let cursorY = row.y + this.padding;
            const barWidth = row.w - this.padding * 2;

            ctx.font = theme.fonts.body;
            ctx.fillStyle = theme.colors.bodyText;
            ctx.textAlign = 'left';
            ctx.fillText(entry.name, left, cursorY);
            ctx.textAlign = 'right';
            ctx.fillText(entry.jobLabel, right, cursorY);
            cursorY += theme.lineHeights.body + 6;

            const hungerRatio = Math.max(0, Math.min(1, entry.hungerRatio));
            const warmthRatio = Math.max(0, Math.min(1, entry.warmthRatio));

            ctx.fillStyle = theme.progress.trackFill;
            ctx.strokeStyle = theme.progress.trackStroke;
            ctx.beginPath();
            ctx.rect(left, cursorY, barWidth, this.barHeight);
            ctx.fill();
            ctx.stroke();
            ctx.fillStyle = theme.colors.barWarn;
            ctx.fillRect(left, cursorY, barWidth * hungerRatio, this.barHeight);

            ctx.font = theme.fonts.muted;
            ctx.fillStyle = theme.colors.mutedText;
            ctx.fillText(entry.hungerLabel, left, cursorY + this.barHeight + 2);

            cursorY += this.barHeight + theme.lineHeights.muted + 6;

            ctx.fillStyle = theme.progress.trackFill;
            ctx.strokeStyle = theme.progress.trackStroke;
            ctx.beginPath();
            ctx.rect(left, cursorY, barWidth, this.barHeight);
            ctx.fill();
            ctx.stroke();
            ctx.fillStyle = theme.colors.barGood;
            ctx.fillRect(left, cursorY, barWidth * warmthRatio, this.barHeight);

            ctx.font = theme.fonts.muted;
            ctx.fillStyle = theme.colors.mutedText;
            ctx.fillText(entry.warmthLabel, left, cursorY + this.barHeight + 2);
        });
    }

    collectHitTargets(_out: HitTarget[], _scrollY: number): void {}
}
